import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface WeeklyMom {
  id: string
  title: string
  meeting_date: string
  time_start: string
  time_end: string
  location: string
  participants: number
  led_by: string
  status: 'selesai' | 'dijadwalkan' | 'dibatalkan'
  divisi: string | null
  topics: string[]
  mom_url: string | null
  attachments_count: number
  notes: string | null
  created_at: string
}

export function useWeeklyMoms(filter: {
  year?: string
  divisi?: string
  search?: string
}) {
  const [moms, setMoms] = useState<WeeklyMom[]>([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(() => {
    setLoading(true)
    let q = supabase
      .from('weekly_moms')
      .select('*')
      .order('meeting_date', { ascending: false })

    if (filter.year && filter.year !== 'semua') {
      q = q
        .gte('meeting_date', `${filter.year}-01-01`)
        .lte('meeting_date', `${filter.year}-12-31`)
    }
    if (filter.divisi && filter.divisi !== 'semua') {
      q = q.eq('divisi', filter.divisi)
    }
    if (filter.search?.trim()) {
      q = q.ilike('title', `%${filter.search.trim()}%`)
    }

    q.then(({ data }) => {
      setMoms(data ?? [])
      setLoading(false)
    })
  }, [filter.year, filter.divisi, filter.search])

  useEffect(() => { fetch() }, [fetch])

  return { moms, loading, refetch: fetch }
}

export function useWeeklyStats(year: string) {
  const [stats, setStats] = useState({ total: 0, selesai: 0, dijadwalkan: 0, dibatalkan: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase
      .from('weekly_moms')
      .select('status')
      .gte('meeting_date', `${year}-01-01`)
      .lte('meeting_date', `${year}-12-31`)
      .then(({ data }) => {
        if (data) {
          setStats({
            total:       data.length,
            selesai:     data.filter(r => r.status === 'selesai').length,
            dijadwalkan: data.filter(r => r.status === 'dijadwalkan').length,
            dibatalkan:  data.filter(r => r.status === 'dibatalkan').length,
          })
        }
        setLoading(false)
      })
  }, [year])

  return { stats, loading }
}

export function useNextSchedule() {
  const [next, setNext] = useState<WeeklyMom | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]
    supabase
      .from('weekly_moms')
      .select('*')
      .eq('status', 'dijadwalkan')
      .gte('meeting_date', today)
      .order('meeting_date', { ascending: true })
      .limit(1)
      .then(({ data }) => {
        setNext(data?.[0] ?? null)
        setLoading(false)
      })
  }, [])

  return { next, loading }
}
